import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import UnifiedCascadingSelect from '../UnifiedCascadingSelect';

/**
 * Step 3 - Application Technique Component
 * Cascading select that shows only techniques available for selected cultures + treatment type
 *
 * @param {Object} props
 * @param {string|null} props.selectedApplicationTechnique - Selected application technique ID
 * @param {Function} props.onSelectApplicationTechnique - Callback when application technique is selected
 * @param {Object} props.selections - Current selections from all steps
 * @param {Array<string>} props.selections.cultures - Selected culture IDs
 * @param {string} props.selections.treatmentType - Selected treatment type ID
 * @param {Object} [props.enrichedData] - Enriched data with cached labels to prevent duplicate API calls
 * @param {Function} [props.onMismatchChange] - Callback when mismatch status changes
 */
const Step3ApplicationTechnique = React.memo(
  ({
    selectedApplicationTechnique,
    onSelectApplicationTechnique,
    selections = {},
    enrichedData = {},
    onMismatchChange,
  }) => {
    const { t } = useTranslation('riskCalculator');

    // Handle technique change - pass option for tags display
    const handleTechniqueChange = useCallback(
      (value, option) => {
        onSelectApplicationTechnique(value, option);
      },
      [onSelectApplicationTechnique]
    );

    return (
      <div className="flex flex-col justify-center items-center gap-4 sm:gap-6 mx-auto w-full px-2 sm:w-[85%] sm:px-0 lg:w-[70%]">
        <h3 className="text-xl sm:text-2xl lg:text-[30px] font-medium text-center normal-case">
          {t('technique_title')}
        </h3>
        {/* Application Technique Cascading Select.
            Rule 41/42/43 (SP-408): the list is already the full "valid
            techniques in the allowed categories" set — no aggregation —
            so the "Voir tous / Voir les X pour ces critères" toggle is
            removed (nothing more to reveal). The DB filter still
            respects BOTH flags per spec: `display_in_risk_index_calculation`
            (the "Affichage dans le Calculateur" checkbox) AND
            `display_on_client_site`. */}
        <div className="step-select-container">
          <UnifiedCascadingSelect
            type="application_technique"
            cultures={selections.cultures || []}
            treatmentType={selections.treatmentType || null}
            value={selectedApplicationTechnique}
            onChange={handleTechniqueChange}
            placeholder={t('technique_select_placeholder')}
            disabled={!selections.treatmentType}
            enrichedLabel={enrichedData?.applicationTechnique?.name}
            onMismatchChange={onMismatchChange}
          />
        </div>
      </div>
    );
  }
);

Step3ApplicationTechnique.displayName = 'Step3ApplicationTechnique';

export default Step3ApplicationTechnique;
